import React from 'react'
import {
  AlignItems,
  Box,
  Color,
  Display,
  FlexDirection,
  FontSize,
  Heading2,
  Heading5,
  ResponsiveValue,
  SpaceScale,
  Text,
} from '@gousto-internal/citrus-react'

import type { CollectionSlug, CollectionUUID } from '@library/menu-service'
import { ClickableBox } from './ClickableBox'
import { RecipeCountBadge } from './RecipeCountBadge'

export type ParentCategoryType = 'generic' | 'promoted'

type VariantConfig = {
  Heading: typeof Heading2 | typeof Heading5
  headingColor?: Color
  descriptionSize: ResponsiveValue<FontSize>
  recipeCountSize: ResponsiveValue<FontSize>
  iconSize: ResponsiveValue<SpaceScale>
  paddingH: ResponsiveValue<SpaceScale>
}

const VARIANT_CONFIG: Record<ParentCategoryType, VariantConfig> = {
  promoted: {
    Heading: Heading2,
    headingColor: Color.ColdGrey_800,
    descriptionSize: 5,
    recipeCountSize: 6,
    iconSize: 8,
    paddingH: 2,
  },
  generic: {
    Heading: Heading5,
    headingColor: undefined,
    descriptionSize: 3,
    recipeCountSize: 1,
    iconSize: 6,
    paddingH: 1,
  },
}

type ParentCategoryHeaderProps = {
  id: CollectionUUID
  slug: CollectionSlug
  description?: string
  isLinkable: boolean
  name: string
  recipeCount: number
  type: ParentCategoryType
  onCategoryClick: (
    categoryId: CollectionUUID,
    slug: CollectionSlug,
    parentCollectionId?: CollectionUUID,
  ) => void
}

export const ParentCategoryHeader = ({
  id,
  slug,
  description,
  isLinkable,
  name,
  recipeCount,
  type,
  onCategoryClick,
}: ParentCategoryHeaderProps) => {
  const { Heading, headingColor, descriptionSize, recipeCountSize, iconSize, paddingH } =
    VARIANT_CONFIG[type] || VARIANT_CONFIG.generic

  return (
    <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={1}>
      <ClickableBox enabled={isLinkable} onClick={() => onCategoryClick(id, slug)}>
        <Box display={Display.Flex} alignItems={AlignItems.Center} gap={3}>
          <Heading color={headingColor} fontWeight={700}>
            {name}
          </Heading>

          {isLinkable && (
            <RecipeCountBadge
              count={recipeCount}
              textSize={recipeCountSize}
              iconSize={iconSize}
              paddingH={paddingH}
            />
          )}
        </Box>
      </ClickableBox>

      {description && (
        <Text color={Color.ColdGrey_900} size={descriptionSize}>
          {description}
        </Text>
      )}
    </Box>
  )
}
